import {DataFetcher, PriorityConfig} from '@aboutyou/data-fetcher';
import {TreeNavigationItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tree_navigation_item.types';
import {useTranslation} from '@aboutyou/i18n';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import React from 'react';
import {
  ExtendedTreeItem,
  NavigationTree,
  NavigationTreeProps,
} from '../../../features/NavigationTree/NavigationTree';
import {NavigationTreeLoading} from '../../../features/NavigationTree/NavigationTreeLoading';
import {useAppliedPotentialFilters} from '../../../features/ProductStream/utils';
import {FIRST_PRODUCT_ID_PARAM} from '../../../features/firstProductId/useFirstProductIds';
import {useBrandShopPath} from '../utils/useBrandShopPath';
import {usePathWithBrandFilter} from '../utils/usePathWithBrandFilter';
import {useBrandShopNavigationTree} from './hooks/useBrandShopNavigationTree';

export const BrandShopNavigationTree: React.FC<
  PriorityConfig & {
    className?: string;
  }
> = ({renderingPriority, ...otherProps}) => {
  const {t} = useTranslation();

  return (
    <DataFetcher
      request={useBrandShopNavigationTree()}
      Error={null}
      Loading={NavigationTreeLoading}
      View={BrandShopNavigationTreeView}
      viewProps={{
        ...otherProps,
        trackOriginKey: 'category_tree_side' as originClick,
        testId: 'brandShopNavigationTree',
        'aria-label': t('Categories'),
        excludeUrlParams: [
          FIRST_PRODUCT_ID_PARAM,
        ] as NavigationTreeProps['excludeUrlParams'],
      }}
      renderingPriority={renderingPriority}
    />
  );
};

const BrandShopNavigationTreeView: React.FC<NavigationTreeProps> = ({
  categoryTree,
  ...otherProps
}) => {
  const normalizeNodes = useNormalizeNodes();
  const hasAppliedFilters = Boolean(
    Object.keys(useAppliedPotentialFilters()).length,
  );

  return (
    <NavigationTree
      categoryTree={categoryTree.map(normalizeNodes)}
      useButtonLink={hasAppliedFilters}
      {...otherProps}
    />
  );
};

const useNormalizeNodes = () => {
  const brandShopPath = useBrandShopPath();
  const categoryPathWithBrandFilter = usePathWithBrandFilter();

  const normalizeNodes = (node: TreeNavigationItem): ExtendedTreeItem => ({
    ...node,
    category: {
      ...node.category,
      path: categoryPathWithBrandFilter(node.category.path),
      targetPath: brandShopPath(node.category.id),
    },
    children: node.children.map(normalizeNodes),
  });

  return normalizeNodes;
};
